import React, { useContext, useEffect, useState } from "react";
import { apiGet, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import OButton from "components/reusable/OButton";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import dayjs from "dayjs";
import Pagination from "../Pagination";
import AuthContext from "context/AuthContext";
import { useTranslation } from "react-i18next";
import ManageAdd from "./ManageAdd";
import ManageList from "./ManageList";
import ManageView from "./ManageView";
import ManageEdit from "./ManageEdit";
import useToastContext from "hooks/useToastContext";
import helpers from "utils/helpers";
import { useLocation } from "react-router-dom";
import OSearch from "components/reusable/OSearch";

const ManageJobs = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const { updatePageName } = useContext(AuthContext);
  const notification = useToastContext();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [jobsAdd, setJobsAdd] = useState(false);
  const [jobsEdit, setJobsEdit] = useState(false);
  const [currentItem, setCurrentItem] = useState("");
  const [jobsView, setJobsView] = useState(false);
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
    isMessage: "",
  });

  const [jobsList, setJobsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [filterData, setFilterData] = useState({
    category: "",
    categoryType: "",
    searchKey: "",
    startDate: "",
    endDate: "",
    isReset: false,
    isFilter: false,
  });
  const [sort, setSort] = useState({
    sortBy: "createdAt",
    sortType: "desc",
  });

  const [categoryList, setCategoryList] = useState("");

  const handleEdit = (item) => {
    setCurrentItem(item);
    setJobsEdit(!jobsEdit);
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  const handleView = (item) => {
    setCurrentItem(item);
    setJobsView(!jobsView);
  };

  const handleCategory = () => {
    if (paginationObj.isMessage) {
      notification.error(paginationObj.isMessage);
      return;
    }
    setJobsAdd((prev) => !prev);
  };

  const getCategoryList = async () => {
    try {
      const res = await apiGet(apiPath.getCategoryList, {});
      setCategoryList(res?.data?.results?.category);
    } catch (error) {
      console.log("error:", error);
    }
  };

  const allJobs = async (data) => {
    try {
      const { category, startDate, endDate, searchKey, isFilter, categoryType } = filterData;
      if ((data?.deletePage && jobsList?.length >= 1) || (isFilter && category && data?.statusChange && jobsList?.length >= 1)) {
        setPage(page - 1);
        setPaginationObj({ ...paginationObj, page: page - 1 });
      }
      const payload = {
        page,
        pageSize,
        status: category,
        startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format("YYYY-MM-DD"), null),
        endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format("YYYY-MM-DD"), null),
        keyword: searchKey,
        sortBy: sort.sortBy,
        sortType: sort.sortType,
        categoryId: categoryType,
      };
      const path = apiPath.manageList;
      const result = await apiGet(path, payload);
      const response = result?.data?.results;
      const resultStatus = result?.data?.success;
      setJobsList(response?.docs);
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, response.page, null),
        pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
        perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs.length, null),
        totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null),
        isMessage: helpers?.ternaryCondition(resultStatus, response.isMessage, null),
      });
    } catch (error) {
      console.log("error in get all jobs list==>>>>", error.message);
    }
  };

  useEffect(() => {
    getCategoryList();
  }, []);

  useEffect(() => {
    allJobs();
  }, [page, filterData, sort]);
  
  const setCategoryType = (e) => {
    setFilterData({
      ...filterData,
      categoryType: e.target.value,
      isFilter: true,
      isReset: false,
    });
    setPage(1);
  };

  const handleReset = () => {
    setFilterData({
      category: "",
      searchKey: "",
      startDate: "",
      endDate: "",
      categoryType: "",
      isReset: true,
      isFilter: false,
    });
    setPage(1);
    setSearchTerm("");
  };

  useEffect(() => {
    updatePageName(t("MANAGE_JOBS"));
  }, []);

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData,
      setFilterData,
      setPage,
    });
  }, [debouncedSearchTerm]);

  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true,
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const statusPageList = (e) => {
    setPage(1);
    setFilterData({ ...filterData, isFilter: true, category: e.target.value });
  };

  useEffect(() => {
    if (location.state) setFilterData({ ...filterData, category: location.state });
  }, [location.state]);

  const handleDeleteJob = async (id) => {
    try {
      const result = await apiDelete(`${apiPath.manageDelete}/${id}`);
      if (result?.data?.success) {
        await allJobs(); // Re-fetch list on successful delete
        notification.success(result?.data?.message);
      }
    } catch (err) {
      console.error("Delete error:", err.message);
    }
  };

  return (
    <>
      {" "}
      <div className="bg-[#F9F9F9]">
        <div className="py-4 px-3 ">
          <div className="bg-white border border-[#E9EDF9] rounded-lg">
            <form className="px-4 gap-2 border-b border-b-[#E3E3E3] 2xl:flex py-3">
              <div className="2xl:mb-0 col-span-2 flex flex-wrap  items-center mb-2 ">
                <div className="flex items-center lg:pt-0 pt-3 flex-wrap ">
                  <div className="relative flex items-center mb-3">
                    <OSearch setSearchTerm={setSearchTerm} placeholder={t("SEARCH_BY_TITLE_CATEGORY")} searchTerm={searchTerm} />
                  </div>
                  <div className="flex items-center  ml-3 mb-3">
                    <select
                      id=""
                      type="text "
                      placeholder=" "
                      onChange={(e) => statusPageList(e)}
                      name="category"
                      className="dark:focus:border-blue-500 block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF]  dark:text-white dark:border-gray-600 focus:outline-none focus:ring-0  peer"
                      value={filterData?.category}
                    >
                      <option value="">
                        {t("ALL_STATUS")}
                      </option>
                      <option value="active">{t("O_ACTIVE")}</option>
                      <option value="inactive">{t("O_INACTIVE")}</option>
                    </select>
                  </div>
                  <div className="flex items-center mb-3 ml-3">
                    <select
                      className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF]  dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
                      placeholder=" "
                      type="select"
                      id="categoryID"
                      value={filterData?.categoryType}
                      onChange={(e) => setCategoryType(e)}
                    >
                      <option value="">{t("SELECT_CATEGORY_NAME")}</option>
                      {categoryList &&
                        categoryList.map((item) => {
                          return <option value={item?._id} key={item?._id}>{item?.name}</option>;
                        })}
                    </select>
                  </div>
                  <ODateRangePicker isReset={filterData?.isReset} handleDateChange={handleDateChange} setIsReset={setFilterData} />
                  <OButton onClick={handleReset} label="Reset" type="button" style={{ borderRadius: "0.5rem" }} extraClasses="ml-3 mb-3" />
                </div>
              </div>
              <div className="ms-auto flex items-center justify-end px-4 mb-3">
                <button onClick={() => handleCategory()} title={t("ADD_MANAGE_JOBS")} type="button" className="bg-LightBlue flex text-sm px-8 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap">
                  + {t("ADD_MANAGE_JOBS")}
                </button>
              </div>
            </form>
            <ManageList jobsList={jobsList} page={page} allJobs={allJobs} handleEdit={handleEdit} handleView={handleView} currentItem={currentItem} jobsView={jobsView} handleDeleteJob ={handleDeleteJob} setSort={setSort} sort={sort} />
            {jobsView && <ManageView jobsView={jobsView} currentItem={currentItem} setJobsView={setJobsView} handleView={handleView} allJobs={allJobs} />}

            {helpers.andCondition(jobsEdit, <ManageEdit allJobs={allJobs} handleEdit={handleEdit} currentItem={currentItem} jobsList={jobsList} />)}
            {helpers.andCondition(jobsAdd, <ManageAdd jobsAdd={jobsAdd} allJobs={allJobs} handleCategory={handleCategory} jobsList={jobsList} />)}
            {helpers.ternaryCondition(paginationObj?.totalItems, <Pagination handlePageClick={handlePageClick} options={paginationObj} page={page} />, null)}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageJobs;
